import { useCallback, useEffect, useState } from "react";
import { api } from "../../lib/api";
import { RefreshCw, CheckCircle2, AlertTriangle, XCircle, ChevronRight } from "lucide-react";
import { RecoveryEmailsModal, RECOVERY_STATUS } from "./RecoveryEmailsModal";

const DOT = { ok: "#16A34A", warn: "#D97706", err: "#DC2626" };

const LABELS = {
  mongodb: "Database",
  gmail: "Email (Gmail)",
  stripe: "Payments (Stripe)",
  recovery: "24h quote recovery emails",
};

const STATUS_TEXT = {
  connected: "Connected",
  valid: "Token valid",
  configured: "Live key set",
  not_connected: "Not connected",
  no_refresh_token: "Reconnect needed",
  invalid: "Token invalid",
  missing: "Key missing",
  unreachable: "Unreachable",
  error: "Error",
};

const OVERALL = {
  ok: { tone: "ok", label: "All systems go" },
  degraded: { tone: "warn", label: "Degraded" },
  error: { tone: "err", label: "Service error" },
};

function recoveryText(c) {
  if (!c) return "—";
  const base = (RECOVERY_STATUS[c.status] || RECOVERY_STATUS.error).label;
  if (c.status === "healthy") return `${base} · ${c.sent_7d ?? 0} sent / 7d${c.due ? ` · ${c.due} due` : ""}`;
  if (c.status === "failing") return `${base} · ${c.failed_7d ?? 0} failed / 7d`;
  return base;
}

export function SystemStatusBadge({ isAdmin }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showRecovery, setShowRecovery] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/health")
      .then((r) => setData(r.data))
      .catch((err) => setData(err?.response?.data || { status: "error", checks: {} }))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const overall = OVERALL[data?.status] || OVERALL.error;
  const Icon = overall.tone === "ok" ? CheckCircle2 : overall.tone === "warn" ? AlertTriangle : XCircle;

  return (
    <div className="border border-[#E5E7EB] bg-white p-4 flex flex-col justify-between" data-testid="system-status-card">
      <div className="flex items-center justify-between mb-2">
        <div className="label-eyebrow">System status</div>
        <button
          data-testid="system-status-refresh"
          onClick={load}
          className="text-[#6B7280] hover:text-[#C79A3B] transition-colors"
          aria-label="Refresh system status"
        >
          <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
        </button>
      </div>

      <div className="flex items-center gap-2 mb-3" data-testid="system-status-overall">
        <Icon size={18} style={{ color: DOT[overall.tone] }} />
        <span className="text-sm font-bold" style={{ color: DOT[overall.tone] }}>
          {loading && !data ? "Checking…" : overall.label}
        </span>
      </div>

      <div className="space-y-1.5">
        {["mongodb", "gmail", "stripe", "recovery"].map((k) => {
          const c = data?.checks?.[k];
          const isRecovery = k === "recovery";
          const tone = c ? (c.ok ? (isRecovery && c.status === "never_run" ? "warn" : "ok") : (data?.status === "error" && k === "mongodb" ? "err" : "warn")) : "warn";
          const status = isRecovery ? recoveryText(c) : (c ? (STATUS_TEXT[c.status] || c.status) : "—");
          const Row = isRecovery ? "button" : "div";
          return (
            <Row key={k} {...(isRecovery ? { type: "button", onClick: () => setShowRecovery(true), title: "Open the recovery-email check" } : {})}
              className={`flex items-center justify-between text-xs w-full ${isRecovery ? "hover:bg-[#FAFAFA] -mx-1 px-1 text-left" : ""}`} data-testid={`system-status-${k}`}>
              <span className="flex items-center gap-2 text-[#12263A]">
                <span className="inline-block w-2 h-2 rounded-full" style={{ backgroundColor: DOT[tone] }} />
                {LABELS[k]}
              </span>
              <span className="text-[#6B7280] inline-flex items-center gap-1">{status}{isRecovery && <ChevronRight size={12} />}</span>
            </Row>
          );
        })}
      </div>
      <RecoveryEmailsModal open={showRecovery} onClose={() => { setShowRecovery(false); load(); }} isAdmin={isAdmin} />
    </div>
  );
}
